import { PHYSICS, MILESTONES } from './constants';

/* ═══════════════════════════════════════════════════════════
   COLOUR HELPERS
   ═══════════════════════════════════════════════════════════ */
function hr(hex) {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}
function lerpN(a, b, t) { return a + (b - a) * t; }
function lerpC(c1, c2, t) { return c1.map((v, i) => Math.round(lerpN(v, c2[i], t))); }
function rgba(c, a) {
  return a != null && a < 1
    ? `rgba(${c[0]},${c[1]},${c[2]},${a})`
    : `rgb(${c[0]},${c[1]},${c[2]})`;
}
function mixHex(h1, h2, t)    { return rgba(lerpC(hr(h1), hr(h2), t)); }
function mixHexRgb(h1, h2, t) { return lerpC(hr(h1), hr(h2), t); }
function darkenRgb(c, k)      { return [Math.round(c[0]*k), Math.round(c[1]*k), Math.round(c[2]*k)]; }

/* ═══════════════════════════════════════════════════════════
   PRAHAR (TIME-OF-DAY) THEMES — Ghibli-anime palettes.
   Always bright, always uplifting. Smooth blend over 120 chants.
   ═══════════════════════════════════════════════════════════ */
const PRAHARS = [
  /* ── 0 · प्रभात · Dawn ── 0 → 240 ── */
  {
    scoreAt: 0,  label: 'प्रभात',
    skyTop:     '#FF8A65', skyMid:     '#FFB74D', skyHorizon: '#FFE0B2',
    sunPos: { xFrac: 0.18, yFrac: 0.62 }, sunR: 28,
    sunColor:   '#FFFDE7', sunGlow:    '#FFD54F',
    cloudColor: '#FFF8E1', cloudEdge:  '#FFB74D',
    hillFar:    '#7E8B9A', hillMid:    '#558B2F', hillNear: '#2E7D32',
    groundTop:  '#BCAAA4', groundBot:  '#6D4C41', groundLine: '#FFD740',
    fenceColor: '#5D4037', fenceTop:   '#8D6E63',
    pillarA:    '#BF360C', pillarB:    '#E64A19', pillarC:  '#FF7043',
    capTop:     '#FFFDE7', capBot:     '#FFD740',
    godRayAlpha: 0.16, starAlpha: 0.06,
  },
  /* ── 1 · प्रातः · Morning ── 240 → 700 ── */
  {
    scoreAt: 20, label: 'प्रातः',
    skyTop:     '#1976D2', skyMid:     '#64B5F6', skyHorizon: '#FFF9C4',
    sunPos: { xFrac: 0.36, yFrac: 0.30 }, sunR: 32,
    sunColor:   '#FFFFFF', sunGlow:    '#FFEB3B',
    cloudColor: '#FFFFFF', cloudEdge:  '#90CAF9',
    hillFar:    '#607D8B', hillMid:    '#4CAF50', hillNear: '#1B5E20',
    groundTop:  '#A1887F', groundBot:  '#5D4037', groundLine: '#F5D76E',
    fenceColor: '#4E342E', fenceTop:   '#795548',
    pillarA:    '#BF360C', pillarB:    '#E64A19', pillarC:  '#FF7043',
    capTop:     '#FFFDE7', capBot:     '#FDD835',
    godRayAlpha: 0.10, starAlpha: 0,
  },
  /* ── 2 · मध्याह्न · Noon ── 700 → 1500 ── */
  {
    scoreAt: 40, label: 'मध्याह्न',
    skyTop:     '#0D47A1', skyMid:     '#1E88E5', skyHorizon: '#E3F2FD',
    sunPos: { xFrac: 0.50, yFrac: 0.18 }, sunR: 38,
    sunColor:   '#FFFFFF', sunGlow:    '#FFFDE7',
    cloudColor: '#FFFFFF', cloudEdge:  '#BBDEFB',
    hillFar:    '#455A64', hillMid:    '#388E3C', hillNear: '#1B5E20',
    groundTop:  '#8D6E63', groundBot:  '#4E342E', groundLine: '#FDD835',
    fenceColor: '#3E2723', fenceTop:   '#6D4C41',
    pillarA:    '#C62828', pillarB:    '#E53935', pillarC:  '#EF5350',
    capTop:     '#FFFFFF', capBot:     '#FDD835',
    godRayAlpha: 0.06, starAlpha: 0,
  },
  /* ── 3 · अपराह्न · Afternoon ── 1500 → 2400 ── */
  {
    scoreAt: 60, label: 'अपराह्न',
    skyTop:     '#1565C0', skyMid:     '#FFB74D', skyHorizon: '#FFE082',
    sunPos: { xFrac: 0.68, yFrac: 0.30 }, sunR: 34,
    sunColor:   '#FFF8E1', sunGlow:    '#FFA726',
    cloudColor: '#FFF3E0', cloudEdge:  '#FFCC80',
    hillFar:    '#546E7A', hillMid:    '#689F38', hillNear: '#33691E',
    groundTop:  '#A1887F', groundBot:  '#5D4037', groundLine: '#FFA726',
    fenceColor: '#4E342E', fenceTop:   '#795548',
    pillarA:    '#BF360C', pillarB:    '#E64A19', pillarC:  '#FF7043',
    capTop:     '#FFF8E1', capBot:     '#FFA726',
    godRayAlpha: 0.13, starAlpha: 0,
  },
  /* ── 4 · सायंकाळ · Sunset ── */
  {
    scoreAt: 80, label: 'सायंकाळ',
    skyTop:     '#5E35B1', skyMid:     '#FF7043', skyHorizon: '#FFE082',
    sunPos: { xFrac: 0.82, yFrac: 0.58 }, sunR: 30,
    sunColor:   '#FFCA28', sunGlow:    '#FF6F00',
    cloudColor: '#FFCCBC', cloudEdge:  '#E65100',
    hillFar:    '#5D4037', hillMid:    '#6D4C41', hillNear: '#3E2723',
    groundTop:  '#8D6E63', groundBot:  '#3E2723', groundLine: '#FF8C00',
    fenceColor: '#3E2723', fenceTop:   '#6D4C41',
    pillarA:    '#4A0000', pillarB:    '#7B0000', pillarC:  '#B71C1C',
    capTop:     '#FFE082', capBot:     '#FF8C00',
    godRayAlpha: 0.18, starAlpha: 0.10,
  },
  /* ── 5 · रात्रि · Night — moonlit dark cycle ── */
  {
    scoreAt: 100, label: 'रात्रि',
    skyTop:     '#060818', skyMid:     '#141432', skyHorizon: '#2A1A3D',
    sunPos: { xFrac: 0.50, yFrac: 0.18 }, sunR: 24,
    sunColor:   '#F0F0FA', sunGlow:    '#9BA8E5',
    cloudColor: '#2A2D4A', cloudEdge:  '#15152E',
    hillFar:    '#1A1A33', hillMid:    '#0F0F25', hillNear: '#080818',
    groundTop:  '#1A0F1F', groundBot:  '#080010', groundLine: '#4A4A8A',
    fenceColor: '#0A0510', fenceTop:   '#1A0F1F',
    pillarA:    '#1A0010', pillarB:    '#3A0020', pillarC:  '#5A0030',
    capTop:     '#B0B4D8', capBot:     '#6A6A9A',
    godRayAlpha: 0.03, starAlpha: 0.55,
  },
];

/* Each prahar lasts PRAHAR_STEP chants. After all 6 (incl. Night), the cycle loops back into Dawn. */
const PRAHAR_STEP = 30;

/* ═══════════════════════════════════════════════════════════
   DRAW HELPERS
   ═══════════════════════════════════════════════════════════ */
function drawCloudPuff(ctx, cx, cy, r) {
  ctx.beginPath();
  ctx.arc(cx,            cy,             r,        0, Math.PI * 2);
  ctx.arc(cx - r * 0.78, cy + r * 0.26,  r * 0.66, 0, Math.PI * 2);
  ctx.arc(cx + r * 0.78, cy + r * 0.26,  r * 0.66, 0, Math.PI * 2);
  ctx.arc(cx - r * 0.36, cy - r * 0.26,  r * 0.52, 0, Math.PI * 2);
  ctx.arc(cx + r * 0.36, cy - r * 0.26,  r * 0.52, 0, Math.PI * 2);
  ctx.arc(cx,            cy - r * 0.38,  r * 0.43, 0, Math.PI * 2);
  ctx.fill();
}

/* ═══════════════════════════════════════════════════════════
   GAME ENGINE
   ═══════════════════════════════════════════════════════════ */
export default class GameEngine {
  constructor(canvas, { hanumanImgSrc, onScore, onGameOver, onMilestone, onReady, onPraharChange }) {
    this.canvas         = canvas;
    this.ctx            = canvas.getContext('2d');
    this.onScore        = onScore        || (() => {});
    this.onGameOver     = onGameOver     || (() => {});
    this.onMilestone    = onMilestone    || (() => {});
    this.onReady        = onReady        || (() => {});
    this.onPraharChange = onPraharChange || (() => {});
    this._lastPraharIdx = -1;

    this.running          = false;
    this.rafId            = null;
    this.bgScroll         = 0;
    this.hanuman          = { x: 0, y: 0, vy: 0 };
    this.obstacles        = [];
    this.ramCount         = 0;
    this._smoothRam       = 0;          // lerps toward ramCount for smooth prahar transitions
    this.obstaclesAvoided = 0;
    this.frameCount       = 0;
    this.milestoneIdx     = 0;
    this.tapTimes         = [];
    this.trail            = [];
    this.ramDialogues     = [];

    /* Frame-cached prahar blend & sun position */
    this._pc          = null;
    this._sunPos      = null;

    /* Pre-built parallax assets (rebuilt on resize) */
    this._cloudLayers = [];
    this._stars       = [];
    this._treeSpacing = 38;

    this.hanumanImg = null;
    if (hanumanImgSrc) {
      const img = new Image();
      img.src = hanumanImgSrc;
      img.onload = () => { this.hanumanImg = img; };
    }

    this._resize = this._resize.bind(this);
    this._resize();
    this._resizeDebounced = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this._resize(), 80);
    };
    window.addEventListener('resize', this._resizeDebounced);
  }

  /* ── Resize + DPR ─────────────────────────────────────── */
  _resize() {
    const canvas = this.canvas;
    const dpr    = Math.min(window.devicePixelRatio || 1, 2);
    const cssW   = canvas.offsetWidth  || window.innerWidth;
    const cssH   = canvas.offsetHeight || window.innerHeight;

    canvas.width  = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this.W = cssW;
    this.H = cssH;

    this.hanuman.x   = Math.floor(cssW * 0.22);
    if (!this.running) this.hanuman.y = Math.floor(cssH * 0.45);
    this.groundY     = cssH - Math.floor(cssH * 0.12);
    this.ceilY       = Math.floor(cssH * 0.04);
    this.hanumanSize = Math.max(44, Math.round(cssH * 0.085));

    this._initBackground();
  }

  /* ── Pre-build static parallax assets ─────────────────── */
  _initBackground() {
    this._buildCloudLayers();
    this._buildStars();
  }

  _buildCloudLayers() {
    const W = this.W, H = this.H;
    const isMobile = W < 640;
    const configs = isMobile
      ? [
        /* MOBILE — fewer, smaller, restricted to upper sky */
        /* far cirrus  */ { count: 3, scrollMult: 0.08, yMin: 0.03, yMax: 0.10, rMin: 22, rMax: 36, aMin: 0.42, aMax: 0.62 },
        /* mid stratus */ { count: 3, scrollMult: 0.20, yMin: 0.06, yMax: 0.14, rMin: 28, rMax: 46, aMin: 0.55, aMax: 0.75 },
        /* near puffs  */ { count: 2, scrollMult: 0.36, yMin: 0.10, yMax: 0.18, rMin: 36, rMax: 56, aMin: 0.70, aMax: 0.86 },
      ]
      : [
        /* DESKTOP / TABLET — original density */
        /* far cirrus  */ { count: 7, scrollMult: 0.08, yMin: 0.04, yMax: 0.16, rMin: 36, rMax: 62,  aMin: 0.50, aMax: 0.72 },
        /* mid stratus */ { count: 6, scrollMult: 0.20, yMin: 0.08, yMax: 0.24, rMin: 50, rMax: 88,  aMin: 0.65, aMax: 0.85 },
        /* near puffs  */ { count: 5, scrollMult: 0.36, yMin: 0.14, yMax: 0.32, rMin: 70, rMax: 118, aMin: 0.78, aMax: 0.95 },
      ];
    this._cloudLayers = configs.map((cfg, idx) => {
      const period = W * 2.4;
      let s = (idx + 1) * 73 + 17;
      const rng = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
      const clouds = [];
      for (let i = 0; i < cfg.count; i++) {
        clouds.push({
          x:     ((i + rng() * 0.85) / cfg.count) * period,
          y:     H * (cfg.yMin + rng() * (cfg.yMax - cfg.yMin)),
          r:     cfg.rMin + rng() * (cfg.rMax - cfg.rMin),
          alpha: cfg.aMin + rng() * (cfg.aMax - cfg.aMin),
        });
      }
      return { clouds, scrollMult: cfg.scrollMult, period };
    });
  }

  _buildStars() {
    const W = this.W, H = this.H;
    this._stars = [];
    for (let i = 0; i < 50; i++) {
      this._stars.push({
        x:            (i * 173.31 * W / 100) % W,
        y:            (i * 83.17  * H / 100) % (H * 0.45),
        r:            0.4 + (i % 4) * 0.42,
        baseAlpha:    0.10 + (i % 6) * 0.07,
        twinklePhase: (i % 7) * 0.45,
      });
    }
  }

  _treeRadius(i) {
    return 11 + ((i * 7919 + 13) % 12); // 11..22
  }

  /* ── Prahar blend — cyclic, smooth-lerped between taps ─ */
  _praharBlend() {
    /* Visual smoothing — _smoothRam crawls toward the actual ramCount
       at 1.5%/frame so a single tap takes ~2 seconds to fully blend in.
       This makes the sky feel like a continuous slow drift rather than
       a discrete jump per tap. */
    this._smoothRam += (this.ramCount - this._smoothRam) * 0.015;

    /* Always-on baseline drift so even when the player pauses, the prahar
       inches forward at ~3 chants/minute — the day-cycle feels alive
       instead of frozen. Capped so it never gets ahead of taps by more
       than half a prahar step. */
    const tapDelta = this.ramCount - this._smoothRam;
    if (tapDelta > -PRAHAR_STEP * 0.5) {
      this._smoothRam += 1 / (60 * 20);  // ≈ 0.0008/frame = 3 chants/min @ 60fps
    }

    const cycleLen = PRAHARS.length * PRAHAR_STEP;           // 6 × 40 = 240
    const ram      = ((this._smoothRam % cycleLen) + cycleLen) % cycleLen;
    const idx      = Math.floor(ram / PRAHAR_STEP);          // 0..5
    const a        = PRAHARS[idx];
    const b        = PRAHARS[(idx + 1) % PRAHARS.length];    // Night → Dawn wraps
    const t        = (ram - idx * PRAHAR_STEP) / PRAHAR_STEP;
    return { a, b, t, idx };
  }

  _calcSunPos() {
    const { a, b, t } = this._pc;
    return {
      x: this.W * lerpN(a.sunPos.xFrac, b.sunPos.xFrac, t),
      y: this.H * lerpN(a.sunPos.yFrac, b.sunPos.yFrac, t),
      r: lerpN(a.sunR, b.sunR, t),
    };
  }

  /* ── Game lifecycle ───────────────────────────────────── */
  start() {
    this.running    = true;
    this.waiting    = true;
    this.waitBaseY  = Math.floor(this.H * 0.45);
    this.bgScroll   = 0;
    this.hanuman.vy = 0;
    this.hanuman.y  = this.waitBaseY;
    this.obstacles  = [];
    this.ramCount   = 0;
    this._smoothRam = 0;
    this._lastPraharIdx = -1;
    this.obstaclesAvoided = 0;
    this.frameCount = 0;
    this.trail      = [];
    this.milestoneIdx = 0;
    this.tapTimes   = [];
    this._loop();
  }

  jump() {
    if (!this.running) return;
    if (this.waiting) {
      this.waiting    = false;
      this.hanuman.vy = 0;
      this.trail      = [];
      this.onReady();
    }
    const now = Date.now();
    this.tapTimes = this.tapTimes.filter(t => now - t < 60000);
    this.tapTimes.push(now);
    if (this.tapTimes.length > PHYSICS.maxTapsPerMin) return;
    this.hanuman.vy = PHYSICS.jumpForce;
    this.ramCount++;
    this.onScore(this.ramCount);
    this._showRamDialogue();
    while (
      this.milestoneIdx < MILESTONES.length &&
      this.ramCount >= MILESTONES[this.milestoneIdx].count
    ) {
      this.onMilestone(MILESTONES[this.milestoneIdx]);
      this.milestoneIdx++;
    }
  }

  _showRamDialogue() {
    const h  = this.hanuman;
    const hs = this.hanumanSize;
    this.ramDialogues.push({
      x:       h.x + hs / 2,
      y:       h.y - 10,
      vy:      -2.2,
      opacity: 1.0,
      text:    'राम',
      size:    22 + Math.random() * 8,
    });
    if (this.ramDialogues.length > 8) this.ramDialogues.shift();
  }

  destroy() {
    this.running = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    clearTimeout(this._resizeTimer);
    window.removeEventListener('resize', this._resizeDebounced);
  }

  /* ── Main loop ────────────────────────────────────────── */
  _loop() {
    if (!this.running) return;
    this.rafId = requestAnimationFrame(() => this._loop());
    this._update();
    this._draw();
    this.frameCount++;
  }

  _update() {
    const h = this.hanuman;

    if (this.waiting) {
      h.y = this.waitBaseY + Math.sin(this.frameCount * 0.04) * 9;
      this.bgScroll += PHYSICS.obstacleSpeed * 0.3;
      return;
    }

    h.vy += PHYSICS.gravity;
    h.y  += h.vy;

    if (h.y + this.hanumanSize >= this.groundY) {
      h.y = this.groundY - this.hanumanSize;
      this._gameOver();
      return;
    }
    if (h.y <= this.ceilY) {
      h.y  = this.ceilY;
      h.vy = 0;
    }

    const minSpacing = this.W * 0.58;
    if (
      this.obstacles.length === 0 ||
      this.W - this.obstacles[this.obstacles.length - 1].x >= minSpacing
    ) {
      this._spawnObstacle();
    }

    for (const obs of this.obstacles) obs.x -= PHYSICS.obstacleSpeed;

    for (const obs of this.obstacles) {
      if (!obs.passed && obs.x + obs.w < h.x) {
        obs.passed = true;
        this.obstaclesAvoided++;
      }
      const hs = this.hanumanSize * PHYSICS.hitboxReduction;
      const hx = h.x + (this.hanumanSize - hs) / 2;
      const hy = h.y + (this.hanumanSize - hs) / 2;
      const inX    = hx < obs.x + obs.w && hx + hs > obs.x;
      const hitTop = inX && hy < obs.topH;
      const hitBot = inX && hy + hs > this.groundY - obs.bottomH;
      if (hitTop || hitBot) { this._gameOver(); return; }
    }

    this.obstacles = this.obstacles.filter(o => o.x + o.w > -20);
    this.bgScroll += PHYSICS.obstacleSpeed;

    this.trail.unshift({ x: h.x, y: h.y });
    if (this.trail.length > 14) this.trail.pop();
  }

  _spawnObstacle() {
    const gap     = Math.max(195, this.groundY * 0.32);
    const minH    = this.groundY * 0.12;
    const maxTop  = this.groundY - gap - minH;
    const topH    = minH + Math.random() * Math.max(10, maxTop - minH);
    const bottomH = this.groundY - topH - gap;
    const w       = Math.max(52, Math.floor(this.W * 0.09));
    this.obstacles.push({ x: this.W + 10, w, topH, bottomH, passed: false });
  }

  _gameOver() {
    this.running = false;
    this.onGameOver({ ramCount: this.ramCount, obstaclesAvoided: this.obstaclesAvoided });
  }

  /* ═════════════════════════════════════════════════════════
     DRAWING — modular cinematic parallax
     ═════════════════════════════════════════════════════════ */
  _draw() {
    const { ctx, W, H } = this;
    this._pc     = this._praharBlend();
    if (this._pc.idx !== this._lastPraharIdx) {
      this._lastPraharIdx = this._pc.idx;
      this.onPraharChange(this._pc.a.label, this._pc.idx);
    }
    this._sunPos = this._calcSunPos();
    ctx.clearRect(0, 0, W, H);

    this._drawSky();
    this._drawStars();
    this._drawGodRays();
    this._drawSun();
    this._drawClouds(0);
    this._drawClouds(1);
    this._drawMountains();
    this._drawHillsMid();
    this._drawClouds(2);
    this._drawHillsNearMass();     // foreground hill mass — green ground for temples
    this._drawTemples();           // temples sitting on foreground hill, behind trees
    this._drawHillsNearTrees();    // trees in front of temples — depth
    this._drawGround();
    this._drawFencePosts();

    this._drawObstacles();
    this._drawHanuman();
    this._drawRamDialogues();
  }

  /* ── Sky · 3-stop vertical gradient ──────────────────── */
  _drawSky() {
    const { ctx, W, H } = this;
    const { a, b, t }   = this._pc;
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0,    mixHex(a.skyTop,     b.skyTop,     t));
    grad.addColorStop(0.55, mixHex(a.skyMid,     b.skyMid,     t));
    grad.addColorStop(1,    mixHex(a.skyHorizon, b.skyHorizon, t));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);
  }

  /* ── Stars · only when starAlpha > 0 ─────────────────── */
  _drawStars() {
    const { a, b, t } = this._pc;
    const alpha = lerpN(a.starAlpha, b.starAlpha, t);
    if (alpha < 0.01) return;
    const { ctx } = this;
    const frame = this.frameCount * 0.018;
    ctx.save();
    ctx.fillStyle = '#FFE8A0';
    for (const s of this._stars) {
      const a2 = (s.baseAlpha + Math.sin(frame + s.twinklePhase) * 0.04) * alpha;
      ctx.globalAlpha = Math.max(0, a2);
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  /* ── God rays · downward fan only (real sunlight feel) ─ */
  _drawGodRays() {
    const { a, b, t } = this._pc;
    const alpha = lerpN(a.godRayAlpha, b.godRayAlpha, t);
    if (alpha < 0.01) return;

    const { ctx, W, H } = this;
    const { x: sx, y: sy } = this._sunPos;
    const glowRgb = mixHexRgb(a.sunGlow, b.sunGlow, t);

    /* Rays fan DOWNWARD from the sun in a ~110° cone, not 360°.
       Canvas angle: π/2 = straight down. ±0.95 rad ≈ ±54° spread. */
    const numRays  = 7;
    const fanHalf  = 0.95;
    const fanWobble = Math.sin(this.frameCount * 0.0008) * 0.08;   // subtle drift, not rotation
    const baseLen  = Math.max(W, H) * 1.4;

    ctx.save();
    ctx.globalCompositeOperation = 'screen';

    for (let i = 0; i < numRays; i++) {
      const tFan   = (i + 0.5) / numRays;                          // 0..1 across the fan
      const angle  = Math.PI / 2 + (tFan - 0.5) * 2 * fanHalf + fanWobble;
      const pulse  = 1 + Math.sin(this.frameCount * 0.014 + i * 0.9) * 0.10;
      const length = baseLen * pulse;
      // edge rays slightly wider, center rays slightly narrower — feels organic
      const edgeBias = Math.abs(tFan - 0.5) * 2;                   // 0 at center, 1 at edges
      const half     = 0.022 + edgeBias * 0.014;

      const cosA  = Math.cos(angle),         sinA  = Math.sin(angle);
      const cosAh = Math.cos(angle + half),  sinAh = Math.sin(angle + half);

      const x0 = sx + cosA  * 4;
      const y0 = sy + sinA  * 4;
      const x1 = sx + cosAh * 4;
      const y1 = sy + sinAh * 4;
      const x2 = sx + cosA  * length;
      const y2 = sy + sinA  * length;
      const x3 = sx + cosAh * length;
      const y3 = sy + sinAh * length;

      // softer center alpha (was 1.6 — caused over-bright stack), faster fade out
      const grad = ctx.createLinearGradient(sx, sy, (x2 + x3) * 0.5, (y2 + y3) * 0.5);
      grad.addColorStop(0,    rgba(glowRgb, alpha * 1.0));
      grad.addColorStop(0.25, rgba(glowRgb, alpha * 0.45));
      grad.addColorStop(1,    rgba(glowRgb, 0));

      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x2, y2);
      ctx.lineTo(x3, y3);
      ctx.lineTo(x1, y1);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  /* ── Sun · disc + corona + inner glow ────────────────── */
  _drawSun() {
    const { ctx } = this;
    const { a, b, t } = this._pc;
    const { x: sx, y: sy, r: sr } = this._sunPos;

    const sunC    = mixHex(a.sunColor, b.sunColor, t);
    const glowC   = mixHex(a.sunGlow,  b.sunGlow,  t);
    const glowRgb = mixHexRgb(a.sunGlow, b.sunGlow, t);

    /* Outer corona */
    const outerR = sr * 5.2;
    const corona = ctx.createRadialGradient(sx, sy, 0, sx, sy, outerR);
    corona.addColorStop(0,    rgba(glowRgb, 0.30));
    corona.addColorStop(0.45, rgba(glowRgb, 0.10));
    corona.addColorStop(1,    rgba(glowRgb, 0));
    ctx.fillStyle = corona;
    ctx.fillRect(sx - outerR, sy - outerR, outerR * 2, outerR * 2);

    /* Inner glow */
    const inner = ctx.createRadialGradient(sx, sy, 0, sx, sy, sr * 1.9);
    inner.addColorStop(0,    sunC);
    inner.addColorStop(0.55, glowC);
    inner.addColorStop(1,    rgba(glowRgb, 0));
    ctx.fillStyle = inner;
    ctx.beginPath();
    ctx.arc(sx, sy, sr * 1.9, 0, Math.PI * 2);
    ctx.fill();

    /* Solid disc */
    ctx.fillStyle = sunC;
    ctx.beginPath();
    ctx.arc(sx, sy, sr, 0, Math.PI * 2);
    ctx.fill();
  }

  /* ── Clouds · 3-layer parallax (Ghibli puffs) ────────── */
  _drawClouds(layerIdx) {
    const layer = this._cloudLayers[layerIdx];
    if (!layer) return;
    const { ctx, W }  = this;
    const { a, b, t } = this._pc;
    const cloudC = mixHex(a.cloudColor, b.cloudColor, t);
    const edgeC  = mixHex(a.cloudEdge,  b.cloudEdge,  t);
    const scroll = this.bgScroll * layer.scrollMult;
    const period = layer.period;

    ctx.save();
    for (const cloud of layer.clouds) {
      const baseX = ((cloud.x - scroll) % period + period) % period;
      for (const off of [0, -period]) {
        const cx = baseX + off;
        if (cx < -cloud.r * 3 || cx > W + cloud.r * 3) continue;

        ctx.globalAlpha = cloud.alpha * 0.32;
        ctx.fillStyle   = edgeC;
        drawCloudPuff(ctx, cx, cloud.y + cloud.r * 0.18, cloud.r * 0.95);

        ctx.globalAlpha = cloud.alpha;
        ctx.fillStyle   = cloudC;
        drawCloudPuff(ctx, cx, cloud.y, cloud.r);
      }
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  /* ── Hill polyline helper ────────────────────────────── */
  _drawHillShape(scrollPos, baseY, amp, freq, fillStyle, alpha) {
    const { ctx, W, H } = this;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle   = fillStyle;
    ctx.beginPath();
    let started = false;
    for (let x = -8; x <= W + 8; x += 4) {
      const p = (x + scrollPos) / freq;
      const raw = 0.50 * Math.sin(p) + 0.30 * Math.sin(p * 1.618 + 1.1) + 0.20 * Math.cos(p * 0.500 + 2.3);
      const y = baseY - amp * (raw + 1) * 0.5;
      if (!started) { ctx.moveTo(x, y); started = true; }
      else            ctx.lineTo(x, y);
    }
    ctx.lineTo(W + 8, H);
    ctx.lineTo(-8,    H);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  _hillY(worldX, baseY, amp, freq) {
    const p = worldX / freq;
    const raw = 0.50 * Math.sin(p) + 0.30 * Math.sin(p * 1.618 + 1.1) + 0.20 * Math.cos(p * 0.500 + 2.3);
    return baseY - amp * (raw + 1) * 0.5;
  }

  /* ── Jagged ridge — for real mountain peaks ──────────── */
  _drawJaggedRidge(scrollPos, baseY, amp, freq, colorTop, colorBase, alpha) {
    const { ctx, W, H } = this;
    ctx.save();
    ctx.globalAlpha = alpha;
    const grad = ctx.createLinearGradient(0, baseY - amp, 0, baseY + amp * 0.4);
    grad.addColorStop(0,   colorTop);
    grad.addColorStop(0.6, colorBase);
    grad.addColorStop(1,   colorBase);
    ctx.fillStyle = grad;

    ctx.beginPath();
    let started = false;
    for (let x = -8; x <= W + 8; x += 2) {
      const p = (x + scrollPos) / freq;
      const macro  = 0.55 * Math.sin(p) + 0.30 * Math.sin(p * 1.618 + 1.1) + 0.20 * Math.cos(p * 0.500 + 2.3);
      const detail = 0.20 * Math.sin(p * 3.7 + 0.8) + 0.13 * Math.sin(p * 7.3 + 1.9);
      const jitter = 0.09 * Math.sin(p * 13.1 + 0.4) + 0.05 * Math.cos(p * 21.7 + 2.1);
      const raw = macro + detail + jitter;
      const y = baseY - amp * (raw + 1) * 0.5;
      if (!started) { ctx.moveTo(x, y); started = true; }
      else            ctx.lineTo(x, y);
    }
    ctx.lineTo(W + 8, H);
    ctx.lineTo(-8,    H);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  /* ── Distant mountain range (3 layers, atmospheric haze) ─ */
  _drawMountains() {
    const { W, H } = this;
    const { a, b, t } = this._pc;
    const farRgb  = mixHexRgb(a.hillFar, b.hillFar, t);
    const skyRgb  = mixHexRgb(a.skyHorizon, b.skyHorizon, t);

    // Atmospheric haze: blend toward sky horizon color for distance fade
    const hazeRgb = (mix) => [
      Math.round(farRgb[0] * (1 - mix) + skyRgb[0] * mix),
      Math.round(farRgb[1] * (1 - mix) + skyRgb[1] * mix),
      Math.round(farRgb[2] * (1 - mix) + skyRgb[2] * mix),
    ];
    const peakRgb = (mix, lighten) => {
      const c = hazeRgb(mix);
      return [
        Math.min(255, Math.round(c[0] + (255 - c[0]) * lighten)),
        Math.min(255, Math.round(c[1] + (255 - c[1]) * lighten)),
        Math.min(255, Math.round(c[2] + (255 - c[2]) * lighten)),
      ];
    };

    const baseY = this.groundY - H * 0.06;

    /* Layer 1 — farthest, tallest jagged peaks, heaviest haze */
    this._drawJaggedRidge(
      this.bgScroll * 0.03,
      baseY - H * 0.04,
      H * 0.28,
      W * 0.42,
      rgba(peakRgb(0.55, 0.20)),
      rgba(hazeRgb(0.35)),
      0.85,
    );

    /* Layer 2 — mid-distance ridge */
    this._drawJaggedRidge(
      this.bgScroll * 0.05 + 120,
      baseY,
      H * 0.22,
      W * 0.30,
      rgba(peakRgb(0.30, 0.12)),
      rgba(hazeRgb(0.15)),
      0.92,
    );

    /* Layer 3 — closer-back ridge, darker, sharper */
    this._drawJaggedRidge(
      this.bgScroll * 0.08 + 60,
      baseY + H * 0.05,
      H * 0.16,
      W * 0.22,
      rgba(peakRgb(0.10, 0.05)),
      rgba(darkenRgb(farRgb, 0.85)),
      1.0,
    );
  }

  /* ── Mid green hills ─────────────────────────────────── */
  _drawHillsMid() {
    const { W, H } = this;
    const { a, b, t } = this._pc;
    const color = mixHex(a.hillMid, b.hillMid, t);
    const baseY = this.groundY - H * 0.04;
    this._drawHillShape(this.bgScroll * 0.18, baseY, H * 0.20, W * 0.26, color, 0.88);
  }

  /* ── Bhagwa (saffron) flag — pole + fluttering triangular flag ─ */
  _drawTempleFlag(x, topY, size) {
    const { ctx } = this;
    ctx.save();
    const poleH = size * 0.55;
    const poleW = Math.max(1.5, size * 0.05);
    const flagW = size * 0.50;
    const flagH = size * 0.30;
    const poleTopY = topY - poleH;

    // pole (dark)
    ctx.fillStyle = '#2E1A14';
    ctx.fillRect(x - poleW / 2, poleTopY, poleW, poleH);

    // gold finial ball on top of pole
    ctx.fillStyle = '#FFD740';
    ctx.beginPath();
    ctx.arc(x, poleTopY - 1.5, Math.max(2, poleW * 1.6), 0, Math.PI * 2);
    ctx.fill();

    // flutter: animate the right tip up/down
    const flutter = Math.sin(this.frameCount * 0.07 + x * 0.05) * size * 0.05;

    // saffron flag body
    ctx.fillStyle = '#FF8C00';
    ctx.beginPath();
    ctx.moveTo(x + poleW / 2,          poleTopY + 2);
    ctx.lineTo(x + poleW / 2 + flagW,  poleTopY + flagH * 0.45 + flutter);
    ctx.lineTo(x + poleW / 2,          poleTopY + flagH);
    ctx.closePath();
    ctx.fill();

    // brighter highlight on the upper-left of the flag (sun side)
    ctx.fillStyle = '#FFB347';
    ctx.globalAlpha = 0.65;
    ctx.beginPath();
    ctx.moveTo(x + poleW / 2 + 0.5,                  poleTopY + 2.5);
    ctx.lineTo(x + poleW / 2 + flagW * 0.55,         poleTopY + flagH * 0.30 + flutter * 0.5);
    ctx.lineTo(x + poleW / 2 + 0.5,                  poleTopY + flagH * 0.55);
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = 1;

    // gold trim along the bottom edge of the flag
    ctx.strokeStyle = '#FFD740';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x + poleW / 2,         poleTopY + flagH);
    ctx.lineTo(x + poleW / 2 + flagW, poleTopY + flagH * 0.45 + flutter);
    ctx.stroke();

    ctx.restore();
  }

  /* ── Temple silhouettes — Nagara / Dravida / Bengali ─── */
  _drawTemple(x, baseY, size, type, bodyFill, highlightFill, goldFill) {
    const { ctx } = this;
    ctx.save();

    if (type === 0) {
      /* NAGARA — North Indian curved shikhara */
      const baseW = size * 1.0;
      const wallH = size * 0.45;
      const shikH = size * 1.10;
      const platformY = baseY;

      // base platform (slightly wider)
      ctx.fillStyle = bodyFill;
      ctx.fillRect(x - baseW * 0.6, platformY - 4, baseW * 1.2, 4);

      // garbhagriha walls
      const wallY = platformY - 4 - wallH;
      ctx.fillRect(x - baseW / 2, wallY, baseW, wallH);

      // door opening (dark slot in wall)
      ctx.fillStyle = highlightFill;
      ctx.globalAlpha = 0.20;
      ctx.fillRect(x - baseW * 0.08, wallY + wallH * 0.25, baseW * 0.16, wallH * 0.75);
      ctx.globalAlpha = 1;

      // curved shikhara (parabolic): drawn via path with quadratic curves on both sides
      ctx.fillStyle = bodyFill;
      ctx.beginPath();
      ctx.moveTo(x - baseW * 0.48, wallY);
      ctx.quadraticCurveTo(x - baseW * 0.18, wallY - shikH * 0.55, x, wallY - shikH);
      ctx.quadraticCurveTo(x + baseW * 0.18, wallY - shikH * 0.55, x + baseW * 0.48, wallY);
      ctx.closePath();
      ctx.fill();

      // left-side highlight along the shikhara curve
      ctx.fillStyle = highlightFill;
      ctx.globalAlpha = 0.30;
      ctx.beginPath();
      ctx.moveTo(x - baseW * 0.48, wallY);
      ctx.quadraticCurveTo(x - baseW * 0.18, wallY - shikH * 0.55, x, wallY - shikH);
      ctx.lineTo(x - baseW * 0.08, wallY - shikH * 0.5);
      ctx.quadraticCurveTo(x - baseW * 0.22, wallY - shikH * 0.30, x - baseW * 0.32, wallY);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1;

      // amalaka disc + kalash
      const kalashY = wallY - shikH;
      ctx.fillStyle = goldFill;
      ctx.beginPath();
      ctx.ellipse(x, kalashY, size * 0.10, size * 0.05, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(x - size * 0.025, kalashY - size * 0.10, size * 0.05, size * 0.06);
      ctx.beginPath();
      ctx.arc(x, kalashY - size * 0.14, size * 0.045, 0, Math.PI * 2);
      ctx.fill();

      // bhagwa flag flying above kalash
      this._drawTempleFlag(x, kalashY - size * 0.19, size);

    } else if (type === 1) {
      /* DRAVIDA — South Indian stepped vimana */
      const baseW = size * 1.20;
      const tier1H = size * 0.30;
      const tier2H = size * 0.28;
      const tier3H = size * 0.26;
      const tier4H = size * 0.22;
      let curY = baseY;

      ctx.fillStyle = bodyFill;
      // base platform
      ctx.fillRect(x - baseW * 0.62, curY - 4, baseW * 1.24, 4);
      curY -= 4;
      // 4 receding tiers
      const tiers = [
        { w: baseW * 1.00, h: tier1H },
        { w: baseW * 0.86, h: tier2H },
        { w: baseW * 0.70, h: tier3H },
        { w: baseW * 0.54, h: tier4H },
      ];
      for (const T of tiers) {
        ctx.fillRect(x - T.w / 2, curY - T.h, T.w, T.h);
        // gold band on top edge of each tier
        ctx.fillStyle = goldFill;
        ctx.globalAlpha = 0.65;
        ctx.fillRect(x - T.w / 2, curY - T.h, T.w, 1.5);
        ctx.globalAlpha = 1;
        // left highlight wedge per tier
        ctx.fillStyle = highlightFill;
        ctx.globalAlpha = 0.25;
        ctx.fillRect(x - T.w / 2, curY - T.h, T.w * 0.18, T.h);
        ctx.globalAlpha = 1;
        ctx.fillStyle = bodyFill;
        curY -= T.h;
      }
      // stupika (small dome cap)
      ctx.fillStyle = goldFill;
      ctx.beginPath();
      ctx.ellipse(x, curY, size * 0.16, size * 0.12, 0, Math.PI, 0);
      ctx.fill();
      // tiny finial
      ctx.fillRect(x - 1, curY - size * 0.14, 2, size * 0.10);

      // bhagwa flag above the finial
      this._drawTempleFlag(x, curY - size * 0.18, size);

    } else {
      /* BENGALI / DOMED — flat-topped wall + hemispherical dome + spire */
      const baseW = size * 0.95;
      const wallH = size * 0.55;
      const platformY = baseY;

      ctx.fillStyle = bodyFill;
      ctx.fillRect(x - baseW * 0.6, platformY - 4, baseW * 1.2, 4);
      const wallY = platformY - 4 - wallH;
      ctx.fillRect(x - baseW / 2, wallY, baseW, wallH);

      // door slot
      ctx.fillStyle = highlightFill;
      ctx.globalAlpha = 0.22;
      ctx.fillRect(x - baseW * 0.10, wallY + wallH * 0.20, baseW * 0.20, wallH * 0.80);
      ctx.globalAlpha = 1;

      // dome
      ctx.fillStyle = bodyFill;
      ctx.beginPath();
      ctx.ellipse(x, wallY, baseW * 0.55, size * 0.45, 0, Math.PI, 0);
      ctx.fill();
      // dome highlight
      ctx.fillStyle = highlightFill;
      ctx.globalAlpha = 0.30;
      ctx.beginPath();
      ctx.ellipse(x - baseW * 0.12, wallY - size * 0.08, baseW * 0.30, size * 0.22, -0.2, Math.PI, 0);
      ctx.fill();
      ctx.globalAlpha = 1;

      // finial: vertical spike + gold ball
      const tipY = wallY - size * 0.45;
      ctx.fillStyle = goldFill;
      ctx.fillRect(x - 1, tipY - size * 0.18, 2, size * 0.18);
      ctx.beginPath();
      ctx.arc(x, tipY - size * 0.22, size * 0.05, 0, Math.PI * 2);
      ctx.fill();

      // bhagwa flag above the dome finial
      this._drawTempleFlag(x, tipY - size * 0.27, size);
    }

    ctx.restore();
  }

  /* ── Place temples on the FOREGROUND (near-hill) ─────────────
     Single deterministic rule, no per-frame visibility filters:
       1. Fixed worldX intervals (consistent on every frame).
       2. Snap each to the local hill-crest in a tight window.
       3. Draw between near-hill mass and trees — green is always
          visible beneath each temple. No floating, no popping. */
  _drawTemples() {
    const { W } = this;
    const { a, b, t } = this._pc;
    const bodyFill      = mixHex(a.pillarA, b.pillarA, t);
    const highlightFill = mixHex(a.pillarB, b.pillarB, t);
    const goldFill      = mixHex(a.capTop,  b.capTop,  t);

    const { scrollPos, baseY, amp, freq } = this._nearHillParams();

    const isMobile    = W < 640;
    const spacing     = isMobile ? 420 : 320;          // wide — temples are landmarks
    const SEARCH_HALF = spacing * 0.22;                // tight: stays near slot center, no crowding
    const SEARCH_STEP = 6;

    const startIdx = Math.floor((scrollPos - spacing) / spacing);
    const endIdx   = Math.ceil( (scrollPos + W + spacing) / spacing);

    for (let i = startIdx; i <= endIdx; i++) {
      const worldX = i * spacing;

      // local-peak snap on the near-hill curve
      let bestX  = worldX;
      let bestHy = this._hillY(worldX, baseY, amp, freq);
      for (let dx = -SEARCH_HALF; dx <= SEARCH_HALF; dx += SEARCH_STEP) {
        const testX  = worldX + dx;
        const testHy = this._hillY(testX, baseY, amp, freq);
        if (testHy < bestHy) { bestHy = testHy; bestX = testX; }
      }

      const screenX = bestX - scrollPos;
      if (screenX < -80 || screenX > W + 80) continue;

      const size = isMobile ? (42 + ((i * 7919 + 41) % 14)) : (50 + ((i * 7919 + 41) % 20));
      const type = ((i * 1597 + 11) % 3 + 3) % 3;
      this._drawTemple(screenX, bestHy + 2, size, type, bodyFill, highlightFill, goldFill);
    }
  }

  /* ── Tree silhouettes — pine / banyan / sal, prahar-tinted ─ */
  _drawTree(x, baseY, size, type, trunkFill, leafFill, leafHi) {
    const { ctx } = this;
    const trunkW = Math.max(2, size * 0.22);
    const trunkH = size * (type === 1 ? 0.55 : 0.45);

    /* trunk */
    ctx.fillStyle = trunkFill;
    ctx.fillRect(x - trunkW / 2, baseY - trunkH, trunkW, trunkH + 1);

    if (type === 0) {
      /* PINE — 3 stacked triangle tiers, broad bottom, narrow top */
      const layers = [
        { y: baseY - trunkH * 0.85, w: size * 1.7, h: size * 0.85 },
        { y: baseY - trunkH * 0.85 - size * 0.40, w: size * 1.35, h: size * 0.80 },
        { y: baseY - trunkH * 0.85 - size * 0.80, w: size * 0.95, h: size * 0.78 },
      ];
      for (const L of layers) {
        // body
        ctx.fillStyle = leafFill;
        ctx.beginPath();
        ctx.moveTo(x - L.w / 2, L.y);
        ctx.lineTo(x + L.w / 2, L.y);
        ctx.lineTo(x,           L.y - L.h);
        ctx.closePath();
        ctx.fill();
        // left-side highlight (catches the directional sun)
        ctx.fillStyle = leafHi;
        ctx.globalAlpha = 0.55;
        ctx.beginPath();
        ctx.moveTo(x - L.w / 2, L.y);
        ctx.lineTo(x - L.w * 0.15, L.y);
        ctx.lineTo(x,             L.y - L.h);
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    } else if (type === 1) {
      /* BANYAN — rounded crown made of 3 overlapping ellipses */
      const cy = baseY - trunkH - size * 0.45;
      const puffs = [
        { dx:  0,           dy:  0,             rx: size * 1.10, ry: size * 0.78 },
        { dx: -size * 0.55, dy:  size * 0.15,   rx: size * 0.70, ry: size * 0.55 },
        { dx:  size * 0.55, dy:  size * 0.15,   rx: size * 0.70, ry: size * 0.55 },
        { dx:  0,           dy: -size * 0.30,   rx: size * 0.85, ry: size * 0.55 },
      ];
      for (const p of puffs) {
        ctx.fillStyle = leafFill;
        ctx.beginPath();
        ctx.ellipse(x + p.dx, cy + p.dy, p.rx, p.ry, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      // upper-left highlight
      ctx.fillStyle = leafHi;
      ctx.globalAlpha = 0.45;
      ctx.beginPath();
      ctx.ellipse(x - size * 0.40, cy - size * 0.35, size * 0.55, size * 0.32, -0.3, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    } else {
      /* SAL — single tall triangular crown */
      const topY  = baseY - trunkH * 0.85 - size * 1.35;
      const baseW = size * 1.45;
      const baseLY = baseY - trunkH * 0.7;
      ctx.fillStyle = leafFill;
      ctx.beginPath();
      ctx.moveTo(x - baseW / 2, baseLY);
      ctx.lineTo(x + baseW / 2, baseLY);
      ctx.lineTo(x,             topY);
      ctx.closePath();
      ctx.fill();
      // inner highlight wedge
      ctx.fillStyle = leafHi;
      ctx.globalAlpha = 0.45;
      ctx.beginPath();
      ctx.moveTo(x - baseW * 0.42, baseLY - 1);
      ctx.lineTo(x - baseW * 0.05, baseLY - 1);
      ctx.lineTo(x,                topY);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1;
    }
  }

  /* ── Foreground hill — shared params (used by mass + trees + temples) ─ */
  _nearHillParams() {
    return {
      scrollPos: this.bgScroll * 0.35,
      baseY:     this.groundY + 2,
      amp:       this.H * 0.10,
      freq:      this.W * 0.18,
    };
  }

  /* ── Near hill — green mass only (drawn first, temples sit on this) ──── */
  _drawHillsNearMass() {
    const { a, b, t } = this._pc;
    const hillFill = rgba(mixHexRgb(a.hillNear, b.hillNear, t));
    const { scrollPos, baseY, amp, freq } = this._nearHillParams();
    this._drawHillShape(scrollPos, baseY, amp, freq, hillFill, 1.0);
  }

  /* ── Near hill trees — drawn AFTER temples so they sit in front of them ─ */
  _drawHillsNearTrees() {
    const { ctx, W } = this;
    const { a, b, t } = this._pc;
    const hillRgb   = mixHexRgb(a.hillNear, b.hillNear, t);
    const leafFill  = rgba(darkenRgb(hillRgb, 0.62));
    const leafHi    = rgba(hillRgb);
    const trunkFill = mixHex(a.fenceColor, b.fenceColor, t);
    const { scrollPos, baseY, amp, freq } = this._nearHillParams();

    ctx.save();
    const isMobile = W < 640;
    const spacing  = isMobile ? 56 : this._treeSpacing;
    const startIdx = Math.floor((scrollPos - spacing) / spacing);
    const endIdx   = Math.ceil( (scrollPos + W + spacing) / spacing);
    for (let i = startIdx; i <= endIdx; i++) {
      const worldX  = i * spacing;
      const screenX = worldX - scrollPos;
      if (screenX < -40 || screenX > W + 40) continue;
      const size = this._treeRadius(i);
      const hy   = this._hillY(worldX, baseY, amp, freq);
      const type = (i * 7919 + 31) % 3;
      this._drawTree(screenX, hy + 2, size, type, trunkFill, leafFill, leafHi);
    }
    ctx.restore();
  }

  /* ── Ground · warm earth strip ───────────────────────── */
  _drawGround() {
    const { ctx, W, H } = this;
    const { a, b, t } = this._pc;
    const groundY = this.groundY;
    const groundH = H - groundY;

    const grad = ctx.createLinearGradient(0, groundY, 0, H);
    grad.addColorStop(0, mixHex(a.groundTop, b.groundTop, t));
    grad.addColorStop(1, mixHex(a.groundBot, b.groundBot, t));
    ctx.fillStyle = grad;
    ctx.fillRect(0, groundY, W, groundH);

    /* Bright divider line + soft glow band */
    const lineC   = mixHex(a.groundLine, b.groundLine, t);
    const glowRgb = mixHexRgb(a.sunGlow,  b.sunGlow,  t);

    ctx.save();
    const lineGrad = ctx.createLinearGradient(0, 0, W, 0);
    lineGrad.addColorStop(0,    'transparent');
    lineGrad.addColorStop(0.05, lineC);
    lineGrad.addColorStop(0.95, lineC);
    lineGrad.addColorStop(1,    'transparent');
    ctx.strokeStyle = lineGrad;
    ctx.lineWidth   = 2.5;
    ctx.beginPath(); ctx.moveTo(0, groundY); ctx.lineTo(W, groundY); ctx.stroke();

    ctx.strokeStyle = rgba(glowRgb, 0.18);
    ctx.lineWidth   = 7;
    ctx.beginPath(); ctx.moveTo(0, groundY); ctx.lineTo(W, groundY); ctx.stroke();
    ctx.restore();
  }

  /* ── Foreground fence posts (depth cue) ──────────────── */
  _drawFencePosts() {
    const { ctx, W, H } = this;
    const { a, b, t } = this._pc;
    const fenceC    = mixHex(a.fenceColor, b.fenceColor, t);
    const fenceTopC = mixHex(a.fenceTop,   b.fenceTop,   t);
    const scroll    = this.bgScroll;
    const groundY   = this.groundY;
    const postSpacing = Math.max(110, W * 0.16);
    const postW = 5;
    const postH = Math.max(34, H * 0.07);

    const startIdx = Math.floor((scroll - postSpacing) / postSpacing);
    const endIdx   = Math.ceil( (scroll + W + postSpacing) / postSpacing);

    ctx.save();
    for (let i = startIdx; i <= endIdx; i++) {
      const worldX  = i * postSpacing;
      const screenX = worldX - scroll;
      if (screenX < -postSpacing || screenX > W + postSpacing) continue;

      ctx.fillStyle = fenceC;
      ctx.fillRect(screenX, groundY - postH, postW, postH);

      ctx.fillStyle = fenceTopC;
      ctx.fillRect(screenX - 1, groundY - postH, postW + 2, 4);

      ctx.fillStyle = fenceC;
      ctx.fillRect(screenX + postW, groundY - postH * 0.55, postSpacing - postW, 2.5);
    }
    ctx.restore();
  }

  /* ═════════════════════════════════════════════════════════
     OBSTACLES — pillar visuals preserved
     ═════════════════════════════════════════════════════════ */
  _drawObstacles() {
    for (const obs of this.obstacles) {
      this._drawPillar(obs.x, obs.w, 0,                          obs.topH,    'top');
      this._drawPillar(obs.x, obs.w, this.groundY - obs.bottomH, obs.bottomH, 'bottom');
    }
  }

  _drawPillar(x, w, y, h, side) {
    const { ctx } = this;
    const { a, b, t } = this._pc;
    const capH = Math.max(18, w * 0.34);
    const capY = side === 'top' ? y + h - capH : y;

    const pA = mixHex(a.pillarA, b.pillarA, t);
    const pB = mixHex(a.pillarB, b.pillarB, t);
    const pC = mixHex(a.pillarC, b.pillarC, t);

    const bodyGrad = ctx.createLinearGradient(x, 0, x + w, 0);
    bodyGrad.addColorStop(0,    pA);
    bodyGrad.addColorStop(0.20, pB);
    bodyGrad.addColorStop(0.50, pC);
    bodyGrad.addColorStop(0.80, pB);
    bodyGrad.addColorStop(1,    pA);
    ctx.fillStyle = bodyGrad;
    ctx.fillRect(x, y, w, h);

    ctx.save();
    ctx.globalAlpha = 0.18;
    ctx.fillStyle   = '#ffffff';
    ctx.fillRect(x + 2, y, 3, h);
    ctx.globalAlpha = 0.06;
    ctx.fillRect(x + w - 5, y, 3, h);
    ctx.restore();

    const bandCount = Math.floor(h / 44);
    if (bandCount > 0) {
      const capTopC = mixHex(a.capTop, b.capTop, t);
      ctx.save();
      ctx.globalAlpha = 0.24;
      const bandGrad = ctx.createLinearGradient(x, 0, x + w, 0);
      bandGrad.addColorStop(0,   'transparent');
      bandGrad.addColorStop(0.3, capTopC);
      bandGrad.addColorStop(0.7, capTopC);
      bandGrad.addColorStop(1,   'transparent');
      ctx.strokeStyle = bandGrad;
      ctx.lineWidth   = 1.2;
      const spacing = h / (bandCount + 1);
      for (let bnd = 1; bnd <= bandCount; bnd++) {
        ctx.beginPath();
        ctx.moveTo(x + 4,     y + spacing * bnd);
        ctx.lineTo(x + w - 4, y + spacing * bnd);
        ctx.stroke();
      }
      ctx.restore();
    }

    const capX      = x - 8;
    const capW      = w + 16;
    const capTopC   = mixHex(a.capTop, b.capTop, t);
    const capBotC   = mixHex(a.capBot, b.capBot, t);
    const capTopRgb = mixHexRgb(a.capTop, b.capTop, t);
    const capBotRgb = mixHexRgb(a.capBot, b.capBot, t);

    ctx.save();
    ctx.globalAlpha = 0.22;
    ctx.fillStyle   = 'rgba(0,0,0,0.5)';
    ctx.fillRect(capX + 2, capY + 2, capW, capH);
    ctx.restore();

    const capGrad = ctx.createLinearGradient(capX, capY, capX, capY + capH);
    capGrad.addColorStop(0,   capTopC);
    capGrad.addColorStop(0.2, capTopC);
    capGrad.addColorStop(0.6, capBotC);
    capGrad.addColorStop(1,   pA);
    ctx.fillStyle = capGrad;
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(capX, capY, capW, capH, [4, 4, 2, 2]);
    else                ctx.rect(capX, capY, capW, capH);
    ctx.fill();

    ctx.save();
    ctx.strokeStyle = rgba(capBotRgb, 0.55);
    ctx.lineWidth   = 1;
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(capX, capY, capW, capH, [4, 4, 2, 2]);
    else                ctx.rect(capX, capY, capW, capH);
    ctx.stroke();
    ctx.strokeStyle = rgba(capTopRgb, 0.55);
    ctx.lineWidth   = 0.8;
    ctx.beginPath();
    ctx.moveTo(capX + 4,        capY + 1);
    ctx.lineTo(capX + capW - 4, capY + 1);
    ctx.stroke();
    ctx.restore();

    const omY = y + h / 2;
    ctx.save();
    ctx.font         = `bold ${Math.min(w * 0.65, 22)}px serif`;
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor  = capTopC;
    ctx.shadowBlur   = 10;
    ctx.fillStyle    = capTopC;
    ctx.globalAlpha  = 0.65;
    ctx.fillText('ॐ', x + w / 2, omY);
    ctx.restore();
  }

  /* ═════════════════════════════════════════════════════════
     HANUMAN + DIALOGUES (untouched)
     ═════════════════════════════════════════════════════════ */
  _drawHanuman() {
    const { ctx } = this;
    const h  = this.hanuman;
    const hs = this.hanumanSize;

    for (let i = this.trail.length - 1; i >= 0; i--) {
      const tr    = this.trail[i];
      const alpha = ((this.trail.length - i) / this.trail.length) * 0.25;
      ctx.save();
      ctx.globalAlpha = alpha;
      if (this.hanumanImg) {
        ctx.drawImage(this.hanumanImg, tr.x, tr.y, hs, hs);
      } else {
        const g = ctx.createRadialGradient(tr.x + hs/2, tr.y + hs/2, 0, tr.x + hs/2, tr.y + hs/2, hs/2);
        g.addColorStop(0, 'rgba(255,149,0,0.7)');
        g.addColorStop(1, 'transparent');
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(tr.x + hs/2, tr.y + hs/2, hs/2, 0, Math.PI * 2); ctx.fill();
      }
      ctx.restore();
    }

    ctx.save();
    const aura = ctx.createRadialGradient(h.x + hs/2, h.y + hs/2, 0, h.x + hs/2, h.y + hs/2, hs * 1.0);
    aura.addColorStop(0,   'rgba(255,200,60,0.35)');
    aura.addColorStop(0.6, 'rgba(255,150,0,0.12)');
    aura.addColorStop(1,   'transparent');
    ctx.fillStyle = aura;
    ctx.beginPath(); ctx.arc(h.x + hs/2, h.y + hs/2, hs * 1.0, 0, Math.PI * 2); ctx.fill();
    ctx.restore();

    ctx.save();
    ctx.shadowBlur  = 24;
    ctx.shadowColor = '#FF9500';
    if (this.hanumanImg) {
      ctx.drawImage(this.hanumanImg, h.x, h.y, hs, hs);
    } else {
      const g = ctx.createRadialGradient(h.x + hs/2, h.y + hs/2, 0, h.x + hs/2, h.y + hs/2, hs/2);
      g.addColorStop(0,   'rgba(255,220,80,0.9)');
      g.addColorStop(0.5, 'rgba(255,107,0,0.6)');
      g.addColorStop(1,   'transparent');
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(h.x + hs/2, h.y + hs/2, hs/2, 0, Math.PI * 2); ctx.fill();
    }
    ctx.restore();
  }

  _drawRamDialogues() {
    const { ctx } = this;
    this.ramDialogues = this.ramDialogues.filter(d => d.opacity > 0.05);
    for (const d of this.ramDialogues) {
      ctx.save();
      ctx.globalAlpha  = d.opacity;
      ctx.font         = `bold ${d.size}px 'Tiro Devanagari Hindi', serif`;
      ctx.fillStyle    = '#FFD700';
      ctx.strokeStyle  = 'rgba(139,0,0,0.7)';
      ctx.lineWidth    = 3;
      ctx.textAlign    = 'center';
      ctx.shadowColor  = '#FF9500';
      ctx.shadowBlur   = 12;
      ctx.strokeText(d.text, d.x, d.y);
      ctx.fillText(d.text, d.x, d.y);
      ctx.restore();
      d.y       += d.vy;
      d.vy      *= 0.97;
      d.opacity -= 0.028;
    }
  }
}
